<template>
    <div :id="editorId" class="editor vue-editor"></div>
</template>

<script>
import ClassicEditor from "@ckeditor/ckeditor5-editor-classic/src/classiceditor";
import Essentials from "@ckeditor/ckeditor5-essentials/src/essentials";
import Blockquote from "@ckeditor/ckeditor5-block-quote/src/blockquote";
import Heading from "@ckeditor/ckeditor5-heading/src/heading";
import Paragraph from "@ckeditor/ckeditor5-paragraph/src/paragraph";
import Bold from "@ckeditor/ckeditor5-basic-styles/src/bold";
import Italic from "@ckeditor/ckeditor5-basic-styles/src/italic";
import Underline from "@ckeditor/ckeditor5-basic-styles/src/underline";
import RemoveFormat from "@ckeditor/ckeditor5-remove-format/src/removeformat";
import Font from "@ckeditor/ckeditor5-font/src/font";
import List from "@ckeditor/ckeditor5-list/src/list";
import Link from "@ckeditor/ckeditor5-link/src/link";
import Alignment from "@ckeditor/ckeditor5-alignment/src/alignment";
import PasteFromOffice from "@ckeditor/ckeditor5-paste-from-office/src/pastefromoffice";
import CKFinder from "@ckeditor/ckeditor5-ckfinder/src/ckfinder";
import MediaEmbed from "@ckeditor/ckeditor5-media-embed/src/mediaembed";
import Image from "@ckeditor/ckeditor5-image/src/image";
import ImageToolbar from "@ckeditor/ckeditor5-image/src/imagetoolbar";
import ImageCaption from "@ckeditor/ckeditor5-image/src/imagecaption";
import ImageStyle from "@ckeditor/ckeditor5-image/src/imagestyle";
import ImageResize from "@ckeditor/ckeditor5-image/src/imageresize";

export default {
    props: {
        value: {
            type: String,
            default: "",
        },
        editorId: {
            type: String,
            default() {
                return "editor-" + Math.random().toString(36).slice(2, 11);
            },
        },
        isReadOnly: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            editorInstance: null,
        };
    },
    watch: {
        value(newValue) {
            if (
                this.editorInstance &&
                newValue !== this.editorInstance.getData()
            ) {
                this.editorInstance.setData(newValue);
            }
        },
        isReadOnly(newValue) {
            if (this.editorInstance) {
                this.editorInstance.isReadOnly = newValue;
            }
        },
    },
    mounted() {
        this.initEditor();
    },
    methods: {
        async initEditor() {
            if (this.editorInstance) {
                await this.editorInstance.destroy();
                this.editorInstance = null;
            }

            const availableFontSizes = [];
            const defaultFontSize = 14;

            for (let i = 8; i <= 24; i++) {
                if (i % 2 !== 0) continue;
                if (i === defaultFontSize) {
                    availableFontSizes.push("default");
                    continue;
                }
                availableFontSizes.push(i);
            }

            this.$nextTick(() => {
                ClassicEditor.create(document.getElementById(this.editorId), {
                    fontSize: { options: availableFontSizes },
                    plugins: [
                        Essentials,
                        Blockquote,
                        Heading,
                        Paragraph,
                        Bold,
                        Italic,
                        Underline,
                        RemoveFormat,
                        Font,
                        List,
                        Alignment,
                        PasteFromOffice,
                        CKFinder,
                        MediaEmbed,
                        Image,
                        ImageToolbar,
                        ImageCaption,
                        ImageStyle,
                        ImageResize,
                        Link,
                    ],
                    toolbar: [
                        "heading",
                        "blockQuote",
                        "|",
                        "bold",
                        "italic",
                        "underline",
                        "removeFormat",
                        "|",
                        "fontColor",
                        "fontSize",
                        "fontFamily",
                        "|",
                        "bulletedList",
                        "numberedList",
                        "alignment",
                        "|",
                        "link",
                        "|",
                        "ckfinder",
                        "mediaEmbed",
                    ],
                    fontFamily: {
                        options: [
                            "default",
                            "Arial, Helvetica, sans-serif",
                            "Arial Black, Arial, Helvetica, sans-serif",
                            "Bradley Hand ITC, Comic Sans MS, cursive",
                            "Courier New, Courier, monospace",
                            "Times New Roman, Times, serif",
                            "Verdana, Geneva, sans-serif",
                        ],
                    },
                    image: {
                        toolbar: [
                            "imageStyle:alignLeft",
                            "imageStyle:full",
                            "imageStyle:alignRight",
                        ],
                        styles: ["full", "alignLeft", "alignRight"],
                    },
                })
                    .then((editor) => {
                        this.editorInstance = editor;
                        this.editorInstance.setData(this.value || ""); // Ensure the editor gets an empty string if value is undefined

                        // Emit changes to v-model
                        this.editorInstance.model.document.on(
                            "change:data",
                            () => {
                                this.$emit(
                                    "input",
                                    this.editorInstance.getData()
                                );
                            }
                        );

                        this.setupCustomFileManager(editor);

                        if (this.isReadOnly) {
                            editor.isReadOnly = true;
                        }
                    })
                    .catch((error) => {
                        console.error(error);
                    });
            });
        },
        setupCustomFileManager(editor) {
            let _fm;
            const appUrl = document.head
                .querySelector('meta[name="app-url"]')
                .getAttribute("content");
            const uploadTargetHash = "l1_Lw";

            const getfm = (open) => {
                return new Promise((resolve, reject) => {
                    const done = () => {
                        if (_fm) {
                            if (open) {
                                _fm.exec("open", open)
                                    .done(() => resolve(_fm))
                                    .fail((err) => reject(_fm, err));
                            } else {
                                resolve(_fm);
                            }
                        } else {
                            _fm = $("<div/>")
                                .dialogelfinder({
                                    title: "File Manager",
                                    url: `${appUrl}/elfinder/connector`,
                                    startPathHash: open || void 0,
                                    autoOpen: false,
                                    width: "80%",
                                    uiOptions: {
                                        toolbar: [
                                            ["back", "forward", "up"],
                                            ["mkdir", "upload", "download"],
                                            ["help"],
                                        ],
                                    },
                                    getFileCallback: (files, fm) => {
                                        let images = [];

                                        // Ensure `files` is an array
                                        const fileArray = Array.isArray(files)
                                            ? files
                                            : [files];

                                        fileArray.forEach((file) => {
                                            // Check if `file` exists and has the `mime` property
                                            if (
                                                file &&
                                                file.mime &&
                                                file.mime.match(/^image\//i)
                                            ) {
                                                images.push(
                                                    fm.convAbsUrl(file.url)
                                                );
                                            } else if (file && file.url) {
                                                editor.execute(
                                                    "link",
                                                    fm.convAbsUrl(file.url)
                                                );
                                            }
                                        });

                                        if (images.length) {
                                            editor.execute("imageInsert", {
                                                source: images,
                                            });
                                        }
                                    },
                                })
                                .elfinder("instance");
                            done();
                        }
                    };
                    done();
                });
            };

            editor.commands.get("ckfinder").execute = () => {
                getfm().then((fm) => {
                    fm.getUI().dialogelfinder("open");
                    $(".dialogelfinder").css("top", "20%");
                });
            };

            editor.plugins.get("FileRepository").createUploadAdapter = (
                loader
            ) => {
                return new (function (loader) {
                    this.upload = () => {
                        return getfm(uploadTargetHash).then((fm) => {
                            return new Promise((resolve, reject) => {
                                fm.exec("upload", {
                                    files: [loader.file],
                                    target: uploadTargetHash,
                                })
                                    .done((data) => {
                                        fm.url(data.added[0].hash, {
                                            async: true,
                                        })
                                            .done((url) => {
                                                resolve({
                                                    default: fm.convAbsUrl(url),
                                                });
                                            })
                                            .fail(() =>
                                                reject("File Not Found")
                                            );
                                    })
                                    .fail(() => reject("Upload Failed"));
                            });
                        });
                    };
                })(loader);
            };
        },
    },
    beforeDestroy() {
        if (this.editorInstance) {
            this.editorInstance
                .destroy()
                .then(() => {
                    this.editorInstance = null;
                })
                .catch((error) => {
                    console.error("Failed to destroy editor instance:", error);
                });
        }
    },
};
</script>

<style scoped>
.editor {
    min-height: 300px;
}
</style>
